import { useState } from "react";
import { useGetIdentity, useNotify } from "ra-core";
import { Link, useNavigate, useParams } from "react-router";
import { Mail, MessageCircle, Phone, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { mailHref, telHref, waHref } from "../contactLinks";
import { startCallTimer } from "../calls/callTimer";
import { promoteMany, promoteMessage } from "../directory/promote";
import { MobilePage } from "../MobilePage";
import { money } from "../pipeline/boardData";
import { OfferDot, StagePill } from "../ui";
import { offerColor, useOffers, type Offer } from "../useOffers";
import { stageLabel } from "../pipeline/useOfferStages";
import { useFicha, type Ficha } from "./useFicha";
import { SendPreview } from "../templates/SendPreview";
import { EnrollButton } from "../sequences/EnrollButton";
import { useQuery } from "@tanstack/react-query";
import { getSupabaseClient } from "../../providers/supabase/supabase";
import { nba, useBestHour } from "../session/playbook";
import {
  PriorityIcon,
  ThermoBar,
  ThermoBreakdown,
  thLevel,
  Trend,
  why,
  type ThermoParts,
} from "../thermo/thermo";
import {
  ActivityTab,
  ContactsTab,
  DataTab,
  DealsTab,
  TasksTab,
} from "./FichaTabs";
import { GuionTab, IntelTab, MatchTab } from "./SalesTabs";
import { ReviewsTab } from "../reviews/reviews";
import { ResearchButton, statusLabel, useResearch } from "../ai/ai";
import { HelpTooltip } from "../help/HelpTooltip";
import { useIsDueno } from "../useIsDueno";

export const DirectoryFicha = () => {
  const { id } = useParams();
  return <Ficha360 dirId={Number(id)} />;
};

export const CompanyFicha = () => {
  const { id } = useParams();
  return <Ficha360 companyId={Number(id)} />;
};

const TABS = [
  ["match", "Match y propuesta"],
  ["guion", "Guion de venta"],
  ["resenas", "Reseñas"],
  ["inteligencia", "Investigación"],
  ["actividad", "Actividad"],
  ["datos", "Datos"],
  ["contactos", "Contactos"],
  ["oportunidades", "Oportunidades"],
  ["tareas", "Tareas"],
] as const;
export type Tab = (typeof TABS)[number][0];

/** Ficha 360 (PRD D.3): la misma para el directorio y para Empresas. */
const Ficha360 = (key: { dirId?: number; companyId?: number }) => {
  const { ficha: f, isPending, refetch } = useFicha(key);
  const [tab, setTab] = useState<Tab>("match");
  const [guionOffer, setGuionOffer] = useState<string | null>(null);
  // Bloque 0b: reviews (SerpApi) are owner-only; a freelancer gets no Reviews tab
  const isDueno = useIsDueno();

  if (isPending) return <p className="p-6 text-muted-foreground">Cargando…</p>;
  if (!f)
    return (
      <p className="p-6 text-muted-foreground">No encontramos esta empresa.</p>
    );
  const openGuion = (slug: string) => {
    setGuionOffer(slug);
    setTab("guion");
    window.scrollTo({ top: 0 });
  };

  return (
    <MobilePage title={f.companyId ? "Empresa" : "Directorio"}>
      <div className="flex flex-col gap-3">
        <Header f={f} refetch={refetch} />
        <div
          className="-mx-1 flex gap-1 overflow-x-auto border-b px-1"
          role="tablist"
        >
          {TABS.filter(([k]) => isDueno || k !== "resenas").map(
            ([k, label]) => (
              <button
                key={k}
                role="tab"
                aria-selected={tab === k}
                onClick={() => setTab(k)}
                className={cn(
                  "-mb-px shrink-0 border-b-2 px-3 py-2 text-sm font-semibold whitespace-nowrap",
                  tab === k
                    ? "border-primary text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground",
                )}
              >
                {label}
                {k === "oportunidades" && ` (${f.deals.length})`}
                {k === "match" && (
                  <HelpTooltip topic="company.offers" className="ml-1" />
                )}
              </button>
            ),
          )}
        </div>
        <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_300px]">
          <div className="min-w-0">
            {tab === "match" && <MatchTab f={f} onGuion={openGuion} />}
            {tab === "guion" && (
              <GuionTab f={f} offer={guionOffer} setOffer={setGuionOffer} />
            )}
            {tab === "resenas" && isDueno && <ReviewsTab f={f} />}
            {tab === "inteligencia" && <IntelTab f={f} />}
            {tab === "actividad" && <ActivityTab f={f} refetch={refetch} />}
            {tab === "datos" && <DataTab f={f} refetch={refetch} />}
            {tab === "contactos" && <ContactsTab f={f} refetch={refetch} />}
            {tab === "oportunidades" && <DealsTab f={f} refetch={refetch} />}
            {tab === "tareas" && <TasksTab f={f} refetch={refetch} />}
          </div>
          <Side f={f} />
        </div>
      </div>
    </MobilePage>
  );
};

/** La oportunidad principal: la primera abierta (o la primera). */
export const mainDeal = (f: Ficha, offers?: Offer[]) =>
  f.deals.find((d) => {
    const o = offers?.find((x) => x.id === d.offer_id);
    return d.stage !== o?.won_stage && d.stage !== o?.lost_stage;
  }) ?? f.deals[0];

const Header = ({ f, refetch }: { f: Ficha; refetch: () => void }) => {
  const { data: offers } = useOffers();
  const main = mainDeal(f, offers);
  const bestFit = offers?.find((o) => f.d?.fits.includes(o.slug));
  const target = {
    template_id: null,
    offer_slug:
      offers?.find((o) => o.id === main?.offer_id)?.slug ??
      bestFit?.slug ??
      null,
    company_name: f.name,
    first_name: f.contacts[0]?.first_name ?? null,
    giro: f.pc.actividad,
    city: f.pc.municipio,
  };
  const tel = f.dnc ? null : telHref(f.phone);
  const wa = f.dnc ? null : waHref(f.phone);
  const mail = f.dnc ? null : mailHref(f.email);
  const btn =
    "inline-flex h-8 items-center gap-1.5 rounded-md border bg-card px-3 text-sm font-semibold";
  const off = "pointer-events-none opacity-50";
  return (
    <div className="flex flex-col gap-2">
      <div className="text-xs font-semibold text-muted-foreground">
        <Link
          to={f.companyId ? "/companies" : "/directory"}
          className="hover:underline"
        >
          {f.companyId ? "Empresas" : "Directorio"}
        </Link>{" "}
        / {main?.key ?? "sin oportunidad"}
      </div>
      <h1 className="text-2xl font-extrabold tracking-tight">{f.name}</h1>
      {f.d?.source.startsWith("denue") && (
        <p className="text-xs text-muted-foreground">Fuente: INEGI (DENUE)</p>
      )}
      <div className="flex flex-wrap gap-1.5">
        {f.deals.map((d) => (
          <span
            key={d.id}
            className="inline-flex items-center gap-1.5 rounded-full border bg-card px-2 py-0.5 text-xs font-semibold"
          >
            <OfferDot
              color={offerColor(offers?.find((o) => o.id === d.offer_id))}
            />
            {d.key} · {stageLabel(d.stage)}
          </span>
        ))}
        {f.dnc && (
          <span className="rounded-full border border-[var(--critical)] px-2 py-0.5 text-xs font-bold text-[var(--critical)]">
            No contactar
          </span>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <a
          href={tel ?? undefined}
          className={cn(btn, !tel && off)}
          aria-disabled={!tel}
          onClick={() => tel && startCallTimer()}
        >
          <Phone className="size-4" /> Llamar
        </a>
        {(["whatsapp", "email"] as const).map((ch) => {
          const ok = ch === "whatsapp" ? wa : mail;
          const Icon = ch === "whatsapp" ? MessageCircle : Mail;
          const label = ch === "whatsapp" ? "WhatsApp" : "Correo";
          return ok ? (
            <SendPreview
              key={ch}
              channel={ch}
              target={target}
              phone={f.phone}
              email={f.email}
              trigger={
                <button className={btn}>
                  <Icon className="size-4" /> {label}
                </button>
              }
            />
          ) : (
            <span key={ch} className={cn(btn, off)} aria-disabled>
              <Icon className="size-4" /> {label}
            </span>
          );
        })}
        {f.companyId && !f.dnc && f.contacts[0] && (
          <EnrollButton
            contactId={f.contacts[0].id}
            companyId={f.companyId}
            offerIds={f.deals.map((d) => d.offer_id)}
          />
        )}
        <span className="mx-1 h-5 w-px bg-border" />
        <ResearchHeader f={f} refetch={refetch} />
        {!f.companyId && f.d && <PromoteMenu f={f} refetch={refetch} />}
      </div>
    </div>
  );
};

/** Investigar con IA + selector de motor y estado de la investigación (PRD G.2). */
const ResearchHeader = ({ f, refetch }: { f: Ficha; refetch: () => void }) => {
  const { data: r } = useResearch(f.d?.id);
  const busy = r?.status === "en_cola" || r?.status === "investigando";
  return (
    <>
      <ResearchButton
        label={
          busy
            ? r?.status === "investigando"
              ? "Investigando…"
              : "En cola"
            : r?.status === "lista"
              ? "Actualizar investigación"
              : "Investigar con IA"
        }
        disabled={busy}
        ids={f.d ? [f.d.id] : undefined}
        resolveIds={
          f.d
            ? undefined
            : async () => {
                // Alta manual sin fila en el directorio: se crea para poder investigarla
                const { data, error } = await getSupabaseClient().rpc(
                  "directory_for_company",
                  { p_company_id: f.companyId },
                );
                if (error) throw new Error(error.message);
                refetch();
                return [data as number];
              }
        }
      />
      {r && r.status !== "lista" && (
        <span className="text-xs text-muted-foreground">{statusLabel(r)}</span>
      )}
    </>
  );
};

/** Pasar al CRM eligiendo el producto (respeta No contactar). */
export const PromoteMenu = ({
  f,
  refetch,
  label = "Pasar al CRM",
}: {
  f: Ficha;
  refetch: () => void;
  label?: string;
}) => {
  const { data: offers } = useOffers();
  const { identity } = useGetIdentity();
  const notify = useNotify();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const fits = f.d?.fits ?? [];
  const list = [...(offers ?? [])].sort(
    (a, b) => Number(fits.includes(b.slug)) - Number(fits.includes(a.slug)),
  );
  const run = async (o: (typeof list)[number]) => {
    setBusy(true);
    const r = await promoteMany([f.d!.id], o, null, Number(identity?.id));
    setBusy(false);
    const [msg, type] = promoteMessage(r, o.name);
    notify(msg, { type });
    refetch();
    if (r.companyIds[0]) navigate(`/companies/${r.companyIds[0]}/show`);
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          size="sm"
          disabled={busy || f.dnc}
          title={f.dnc ? "Está en No contactar" : undefined}
        >
          <Plus /> {busy ? "Pasando…" : label}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {list.map((o) => (
          <DropdownMenuItem key={o.id} onClick={() => run(o)}>
            <OfferDot color={offerColor(o)} />
            {o.name}
            {fits.includes(o.slug) && (
              <span className="ml-auto text-xs text-muted-foreground">
                encaja
              </span>
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const Side = ({ f }: { f: Ficha }) => {
  const { identity } = useGetIdentity();
  const { data: offers } = useOffers();
  const main = mainDeal(f, offers);
  const row = (k: React.ReactNode, v: React.ReactNode) => (
    <>
      <dt className="text-muted-foreground">{k}</dt>
      <dd className="font-semibold">{v}</dd>
    </>
  );
  return (
    <aside className="flex flex-col gap-3" aria-label="Detalles">
      <ThermoPanel f={f} />
      <section className="rounded-xl border bg-card p-3.5">
        <h4 className="mb-2 text-xs font-bold uppercase tracking-wide text-muted-foreground">
          Detalles
        </h4>
        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-sm">
          {row("Responsable", identity?.fullName ?? "—")}
          {main ? (
            <>
              {row("Clave", <span className="font-mono">{main.key}</span>)}
              {row(
                "Prioridad",
                <PriorityIcon
                  thermo={main.thermo}
                  offerPriority={main.offer_priority}
                  withLabel
                />,
              )}
              {row(
                <span className="inline-flex items-center gap-1">
                  Etapa
                  <HelpTooltip topic="deal.stage" />
                </span>,
                <StagePill stage={main.stage} />,
              )}
              {row("Valor", money(main.amount))}
              {row("Probabilidad", `${Math.round(main.prob * 100)}%`)}
              {row("Pronóstico", money(Math.round(main.amount * main.prob)))}
              {row(
                "Próximo paso",
                main.next_step_at
                  ? main.next_step_at.split("-").reverse().join("/")
                  : "—",
              )}
              {row("Días sin contacto", main.idle_days ?? "—")}
            </>
          ) : (
            row("En el CRM", f.companyId ? "Sin oportunidades" : "Todavía no")
          )}
          {f.d &&
            row(
              <span className="inline-flex items-center gap-1">
                Nivel
                <HelpTooltip topic="company.tier" />
              </span>,
              `${f.d.tier ?? "—"} · prioridad ${f.d.priority}`,
            )}
          {f.d && row("Fuente", f.d.source)}
          {row(
            "Investigada",
            f.d?.researched_at
              ? new Date(f.d.researched_at).toLocaleDateString("es-MX")
              : "No",
          )}
        </dl>
      </section>
    </aside>
  );
};

/** Termómetro de éxito (PRD E.1–E.4): grande, desglose, por qué, acción y por producto. */
const ThermoPanel = ({ f }: { f: Ficha }) => {
  const { data: offers } = useOffers();
  const bestHour = useBestHour();
  const main = mainDeal(f, offers);
  const { data: byOffer } = useQuery({
    queryKey: ["directory_thermo", f.d?.id],
    enabled: !!f.d,
    queryFn: async () => {
      const { data, error } = await getSupabaseClient().rpc(
        "directory_thermo",
        { p_id: f.d!.id },
      );
      if (error) throw error;
      return data as {
        offer_id: number;
        slug: string;
        value: number;
        parts: ThermoParts;
      }[];
    },
  });
  const fits = f.d?.fits ?? [];
  // Por producto: el de su oportunidad si la tiene; si no, el del directorio
  const list = (offers ?? [])
    .map((o) => {
      const deal = f.deals.find((d) => d.offer_id === o.id);
      const dir = byOffer?.find((x) => x.offer_id === o.id);
      return {
        o,
        deal,
        fit: fits.includes(o.slug),
        value: deal?.thermo ?? dir?.value ?? null,
        parts: deal?.thermo_parts ?? dir?.parts ?? null,
      };
    })
    .filter((x) => x.value != null)
    .sort((a, b) => b.value! - a.value!);
  // Mejor producto para entrar (bestOffers del prototipo): termómetro − 6 por nivel de prioridad
  const best = list
    .filter((x) => x.fit)
    .sort(
      (a, b) =>
        b.value! - (b.o.priority - 1) * 6 - (a.value! - (a.o.priority - 1) * 6),
    )[0];
  const head = main
    ? {
        value: main.thermo,
        parts: main.thermo_parts,
        decay: main.thermo_decay,
        idle: main.idle_days,
        label: `${offers?.find((o) => o.id === main.offer_id)?.name ?? ""} · ${main.key}`,
      }
    : best
      ? {
          value: best.value,
          parts: best.parts,
          decay: 0,
          idle: null,
          label: `${best.o.name} (sin oportunidad)`,
        }
      : null;
  const w = head?.parts ? why(head.parts, head.decay ?? 0) : null;
  const level = head?.value != null ? thLevel(head.value) : null;
  const action = nba({
    dnc: f.dnc,
    stage: main?.stage,
    offerName: offers?.find((o) => o.id === main?.offer_id)?.name,
    bestOfferName: best?.o.name,
    idle: main?.idle_days,
    decay: main?.thermo_decay,
    hora: bestHour(f.pc.sector),
  });
  const box = "rounded-xl border bg-card p-3.5";
  const h4 =
    "mb-2 text-xs font-bold uppercase tracking-wide text-muted-foreground";
  return (
    <>
      <section className={box}>
        <h4 className={h4}>Termómetro de éxito</h4>
        {head?.value != null && level ? (
          <div className="flex flex-col gap-3">
            <div className="flex items-end gap-3">
              <b
                className="text-5xl leading-none font-extrabold tabular-nums"
                style={{ color: `var(${level[2]})` }}
              >
                {head.value}
              </b>
              <div className="pb-1">
                <div className="font-bold">{level[1]}</div>
                <div className="text-xs text-muted-foreground">
                  {head.label}
                </div>
              </div>
            </div>
            <span className="h-2 overflow-hidden rounded-full bg-secondary">
              <i
                className="block h-full rounded-full"
                style={{
                  width: `${head.value}%`,
                  background: `var(${level[2]})`,
                }}
              />
            </span>
            {main ? (
              <Trend
                weeks={main.trend_weeks}
                value={main.thermo}
                change={main.trend_2w}
                big
              />
            ) : null}
            {head.parts && (
              <ThermoBreakdown
                parts={head.parts}
                decay={head.decay}
                idle={head.idle}
              />
            )}
            {w && (
              <div className="flex flex-col gap-1 rounded-lg bg-secondary p-2.5 text-sm">
                <span>
                  <b>Sube por:</b> {w.up}.
                </span>
                <span>
                  <b>Le falta:</b> {w.low}.
                </span>
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            Sin datos del directorio para calcularlo: cuenta solo la interacción
            cuando tenga oportunidades.
          </p>
        )}
      </section>
      <section className={cn(box, "border-primary/40")}>
        <h4 className={h4}>Siguiente mejor acción</h4>
        <p className="text-sm">{action}</p>
      </section>
      {list.length > 0 && (
        <section className={box}>
          <h4 className={h4}>Termómetro por producto</h4>
          <ul className="flex flex-col gap-1.5 text-sm">
            {list.map((x) => (
              <li
                key={x.o.id}
                className="grid grid-cols-[1fr_auto] items-center gap-2"
              >
                <span className="flex min-w-0 items-center gap-1.5">
                  <OfferDot color={offerColor(x.o)} />
                  <span className="truncate">{x.o.name}</span>
                  {x.deal ? (
                    <span className="font-mono text-[11px] text-muted-foreground">
                      {x.deal.key}
                    </span>
                  ) : (
                    !x.fit && (
                      <span className="text-[11px] text-muted-foreground">
                        no encaja
                      </span>
                    )
                  )}
                </span>
                <ThermoBar value={x.value} mini />
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  );
};
